import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building, Gift, Users } from 'lucide-react';
import { Avatar } from '@clareefai/ui';
import KanbanBoard from '../shared/KanbanBoard';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { Promoter, KanbanItem } from '@clareefai/types';
import type { PromoterResponse } from '../../store/promoterApi';

type DerivedProgress = {
  reviewCompleted: boolean;
  testimonialCompleted: boolean;
  useCaseCompleted: boolean;
  firstMeetingCompleted: boolean;
};

/** Derive progress from what promoters added/uploaded (testimonials) and calls (meetings). */
const deriveProgressFromTestimonialsAndMeetings = (response: PromoterResponse): DerivedProgress => {
  const testimonials = response.testimonials ?? [];
  const meetings = response.meetings ?? [];

  const reviewCompleted = testimonials.some((t) => t.type === 'REVIEW');
  const testimonialCompleted = testimonials.some((t) => t.type === 'VIDEO');
  const useCaseCompleted = testimonials.some((t) => t.type === 'USE_CASE');

  const firstMeetingCompleted = meetings.some((m) => {
    const s = (m.status ?? '').toUpperCase();
    if (s === 'COMPLETED' || s === 'PARTIALLY_ATTENDED') return true;
    return !!(m.meeting_started_at ?? m.prospect_joined_at ?? m.promoter_joined_at);
  });

  return {
    reviewCompleted,
    testimonialCompleted,
    useCaseCompleted,
    firstMeetingCompleted,
  };
};

// Helper function to map backend PromoterResponse to frontend Promoter
const mapPromoterResponseToPromoter = (response: PromoterResponse): Promoter => {
  // Determine status based on derived progress from testimonials and meetings
  let status: Promoter['status'] = 'invited';

  const { reviewCompleted, testimonialCompleted, useCaseCompleted, firstMeetingCompleted } =
    deriveProgressFromTestimonialsAndMeetings(response);

  if (firstMeetingCompleted && useCaseCompleted && testimonialCompleted && reviewCompleted) {
    status = 'available';
  } else if (useCaseCompleted && testimonialCompleted && reviewCompleted) {
    status = 'testimonial_done';
  } else if (testimonialCompleted && reviewCompleted) {
    status = 'testimonial_ready';
  } else if (reviewCompleted) {
    status = 'review';
  } else if (response.status) {
    // Fallback to status field if no testimonials/meetings found
    const statusMap: Record<string, Promoter['status']> = {
      'invited': 'invited',
      'review': 'review',
      'testimonial_ready': 'testimonial_ready',
      'testimonial_done': 'testimonial_done',
      'available': 'available'
    };
    status = statusMap[response.status] || 'invited';
  }

  return {
    id: response.id || '',
    name: `${response.firstName} ${response.lastName}`,
    email: response.email,
    phone: response.phone || '',
    company: response.company || '',
    avatar: response.avatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(`${response.firstName} ${response.lastName}`),
    status,
    prospectsReferred: response.prospectsReferred || 0,
    conversionRate: response.conversionRate || 0,
    totalRewards: response.totalRewards || 0,
    joinedDate: response.joinedDate || response.createdAt || '',
    importSource: (response.importSource as Promoter['importSource']) ?? null,
  };
};

type FilterMode = 'all' | 'imported';

const PromotersKanban: React.FC = () => {
  const { t } = useTranslation();
  const { data: promotersData, isLoading, error } = useGetPromotersQuery();
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const navigate = useNavigate();

  // Map backend data to frontend format
  const promoters = promotersData ? promotersData.map(mapPromoterResponseToPromoter) : [];

  const filteredPromoters = filterMode === 'imported'
    ? promoters.filter(p => p.importSource != null)
    : promoters;

  const columns = [
    {
      id: 'invited',
      title: t('promoters.invited'),
      items: filteredPromoters.filter(p => p.status === 'invited') as KanbanItem[],
      color: 'bg-yellow-100 text-yellow-800',
      headerColor: 'bg-gradient-to-r from-yellow-500 to-orange-500',
      borderColor: '#eab308'
    },
    {
      id: 'review',
      title: t('promoters.review'),
      items: filteredPromoters.filter(p => p.status === 'review') as KanbanItem[],
      color: 'bg-blue-100 text-blue-800',
      headerColor: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      borderColor: '#3b82f6'
    },
    {
      id: 'testimonial_ready',
      title: t('promoters.testimonialReady'),
      items: filteredPromoters.filter(p => p.status === 'testimonial_ready') as KanbanItem[],
      color: 'bg-green-100 text-green-800',
      headerColor: 'bg-gradient-to-r from-green-500 to-emerald-500',
      borderColor: '#10b981'
    },
    {
      id: 'testimonial_done',
      title: t('promoters.testimonialDone'),
      items: filteredPromoters.filter(p => p.status === 'testimonial_done') as KanbanItem[],
      color: 'bg-purple-100 text-purple-800',
      headerColor: 'bg-gradient-to-r from-purple-500 to-violet-500',
      borderColor: '#8b5cf6'
    },
    {
      id: 'available',
      title: t('promoters.available'),
      items: filteredPromoters.filter(p => p.status === 'available') as KanbanItem[],
      color: 'bg-red-100 text-red-800',
      headerColor: 'bg-gradient-to-r from-red-500 to-pink-500',
      borderColor: '#ef4444'
    }
  ];

  const handlePromoterClick = (promoterId: string) => {
    navigate(`/promoters/${promoterId}`);
  };

  const renderCard = (item: KanbanItem) => {
    const promoter = item as Promoter;
    return (
      <div
        className="space-y-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
        onClick={() => handlePromoterClick(promoter.id)}
      >
        <div className="flex items-center space-x-3">
          <Avatar
            src={promoter.avatar}
            name={promoter.name}
            size="md"
          />
          <div>
            <h4 className="font-medium text-gray-900">{promoter.name}</h4>
            <p className="text-sm text-gray-600 flex items-center">
              <Building className="w-3 h-3 mr-1" />
              {promoter.company}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="text-center">
            <div className="font-semibold text-blue-600">{promoter.prospectsReferred ?? 0}</div>
            <div className="text-gray-500 flex items-center justify-center">
              <Users className="w-3 h-3 mr-0.5" />
              {t('promoters.kanbanBoard.prospects')}
            </div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-green-600">{(promoter.totalRewards ?? 0).toLocaleString()}€</div>
            <div className="text-gray-500 flex items-center justify-center">
              <Gift className="w-3 h-3 mr-0.5" />
              {t('promoters.kanbanBoard.rewards')}
            </div>
          </div>
        </div>

        <div className="pt-2">
          <span className={`status-badge ${
            promoter.status === 'invited' ? 'status-invited' :
            promoter.status === 'review' ? 'status-review' :
            promoter.status === 'testimonial_ready' ? 'status-available' :
            promoter.status === 'testimonial_done' ? 'status-completed' :
            'status-failed'
          }`}>
            {promoter.status === 'invited' ? t('promoters.kanbanBoard.status.invited') :
             promoter.status === 'review' ? t('promoters.kanbanBoard.status.reviewed') :
             promoter.status === 'testimonial_ready' ? t('promoters.kanbanBoard.status.testimonialReady') :
             promoter.status === 'testimonial_done' ? t('promoters.kanbanBoard.status.testimonialDone') :
             t('promoters.kanbanBoard.status.available')}
          </span>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">{t('promoters.kanbanBoard.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500">{t('promoters.kanbanBoard.loadError')}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <div className="inline-flex rounded-[10px] bg-[rgba(241,241,241,0.8)] p-1">
          <button
            onClick={() => setFilterMode('all')}
            className={`px-3 py-1.5 text-sm font-medium rounded-[8px] transition-all ${
              filterMode === 'all'
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('promoters.kanbanBoard.filterAll')}
          </button>
          <button
            onClick={() => setFilterMode('imported')}
            className={`px-3 py-1.5 text-sm font-medium rounded-[8px] transition-all ${
              filterMode === 'imported'
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('promoters.kanbanBoard.filterImported')}
          </button>
        </div>
      </div>
      <KanbanBoard
        columns={columns}
        renderCard={renderCard}
      />
    </div>
  );
};

export default PromotersKanban;
